import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { OpportunityForm as F } from '@percent/domain/volunteering'
import { config } from '@percent/cause-dashboard/config'

import { useVoluteeringCountries } from '../hooks/useVoluteeringCountries'
import { Alpha3Code } from 'i18n-iso-countries'
import { uniq } from 'lodash'
import type { UsePartnerConfigResult } from '../hooks/usePartnerConfig'

type LocationSectionProps = Pick<
  F.LocationSectionProps,
  | 'value'
  | 'touched'
  | 'errors'
  | 'handleFormChange'
  | 'setTouched'
  | 'onBlur'
  | 'validate'
  | 'preFillAddressLookup'
  | 'onChange'
> & { partnerConfig: UsePartnerConfigResult }

export function LocationSection({ partnerConfig, ...props }: Readonly<LocationSectionProps>) {
  const { t } = useTranslation()
  const { countries, countryHelpers } = useVoluteeringCountries()

  const countryCodes = useMemo(() => countries.map(country => country.code), [countries])

  const regionsGroupedOptions = useMemo(() => {
    if (!partnerConfig.regions) return undefined

    return Object.values(partnerConfig.regions).reduce((acc, region) => {
      acc[region.name] = uniq(region.countryCodes).map(countryCode => ({
        label: countryHelpers.getName(countryCode as Alpha3Code, 'en'),
        value: countryCode
      }))

      return acc
    }, {} as Record<string, Array<{ label: string; value: string }>>)
  }, [partnerConfig.regions, countryHelpers])

  return (
    <F.LocationSection
      {...props}
      allowedCountryCodes={countryCodes}
      countryHelpers={countryHelpers}
      googleMapsApiKey={config.keys.googleMaps}
      regionsGroupedOptions={regionsGroupedOptions}
      sectionTitle={t('volunteering.opportunity.form.common.locationSection.title')}
      locationTypeLabel={t('volunteering.opportunity.form.common.locationSection.locationTypeField.label') as string}
      locationTypePlaceholder={t('volunteering.opportunity.form.common.locationSection.locationTypeField.placeholder')}
      virtualLocationTypeLabel={t('volunteering.opportunity.form.common.locationSection.locationTypeField.virtual')}
      inPersonLocationTypeLabel={t('volunteering.opportunity.form.common.locationSection.locationTypeField.offline')}
      videoConferencingLabel={t('volunteering.opportunity.form.common.locationSection.videoConferencingField.label')}
      videoConferencingPlaceholder={t(
        'volunteering.opportunity.form.common.locationSection.videoConferencingField.placeholder'
      )}
      targetCountriesLabel={t('volunteering.opportunity.form.common.locationSection.targetCountriesField.label')}
      targetCountriesDescription={t(
        'volunteering.opportunity.form.common.locationSection.targetCountriesField.description'
      )}
      targetCountriesPlaceholder={t(
        'volunteering.opportunity.form.common.locationSection.targetCountriesField.placeholder'
      )}
      targetCountriesSearchPlaceholder={t(
        'volunteering.opportunity.form.common.locationSection.targetCountriesField.search.placeholder'
      )}
      targetCountriesSelectAllLabel={t(
        'volunteering.opportunity.form.common.locationSection.targetCountriesField.selectAll'
      )}
      targetCountriesFooterTranslation={count =>
        t('volunteering.opportunity.form.common.locationSection.targetCountriesField.countriesSelected', { count })
      }
      lookupLocationLabel={t('volunteering.opportunity.form.common.locationSection.locationLookupField.label')}
      streetAddressLabel={t('volunteering.opportunity.form.common.locationSection.streetAddressField.label')}
      streetAddressPlaceholder={t(
        'volunteering.opportunity.form.common.locationSection.streetAddressField.placeholder'
      )}
      apartmentLabel={t('volunteering.opportunity.form.common.locationSection.apartmentField.label')}
      apartmentPlaceholder={t('volunteering.opportunity.form.common.locationSection.apartmentField.placeholder')}
      cityLabel={t('volunteering.opportunity.form.common.locationSection.cityField.label')}
      cityPlaceholder={t('volunteering.opportunity.form.common.locationSection.cityField.placeholder')}
      countryLabel={t('volunteering.opportunity.form.common.locationSection.countryField.label')}
      countryPlaceholder={t('volunteering.opportunity.form.common.locationSection.countryField.placeholder')}
      zipOrPostalCodeLabel={t('volunteering.opportunity.form.common.locationSection.zipCodeField.label')}
      zipOrPostalCodePlaceholder={t('volunteering.opportunity.form.common.locationSection.zipCodeField.placeholder')}
    />
  )
}
